import { FC } from 'react';

import { Box, BoxProps, Grid, Img, Text, keyframes } from '@chakra-ui/react';
import { Booster, Card } from 'models';
import getInitials from 'utils/getInitials';

import BenchIcon from 'icons/bench.svg';
import BoosterIcon from 'icons/boost.svg';
import UserIcon from 'icons/user.svg';

type CardPlaceholderProps = {
  card?: Card;
  booster?: Booster;
  showAnimation?: boolean;
  noName?: boolean;
  type?: 'start' | 'bench' | 'booster';
  fit?: 'cover' | 'contain';
  noLabel?: boolean;
} & BoxProps;

const onLoadAnimation = keyframes`
  0% {
    scale: 1;
    opacity: 1;
  }
  99% {
    scale: 1.5;
    opacity: 0.3;
  }
  100% {
    display: none;
  }
`;

const CardPlaceholder: FC<CardPlaceholderProps> = ({
  card,
  booster,
  showAnimation,
  noName,
  type = 'start',
  fit = 'cover',
  noLabel = false,
  ...props
}) => {
  const headshot =
    card?.headshotThumbnails?.small ?? card?.headshotThumbnails?.medium ?? card?.headshotThumbnails?.original ?? '';
  const iconSize = (type: 'start' | 'bench' | 'booster') => {
    if (type === 'booster') {
      return '16px';
    } else if (type === 'bench') {
      return '30px';
    }
  };
  const getDisplayContent = () => {
    if (type === 'booster') {
      if (booster) {
        return {
          backgroundImage: `url(${booster.image})`,
          backgroundColor: booster.backgroundColor ?? 'transparent',
          backgroundSize: 'cover',
          icon: booster.icon,
          showIcon: !!booster.icon,
        };
      } else {
        return {
          backgroundImage: `url(${BoosterIcon})`,
          backgroundColor: '#343434',
          backgroundSize: iconSize(type),
          icon: null,
          showIcon: false,
        };
      }
    } else if (card) {
      return {
        backgroundImage: `url(${headshot})`,
        backgroundColor: type === 'bench' ? '#343434' : 'white',
        backgroundSize: 'cover',
        icon: null,
        showIcon: false,
      };
    } else {
      const icon = type === 'bench' ? BenchIcon : UserIcon;
      return {
        backgroundImage: `url(${icon})`,
        backgroundColor: '#343434',
        backgroundSize: iconSize(type),
        icon: null,
        showIcon: false,
      };
    }
  };

  const displayContent = getDisplayContent();

  const getDisplayText = () => {
    if (type === 'booster' && booster) {
      return `${booster.attributeMultiplier}x${booster.attributeDisplay}`;
    } else if (card?.playerName) {
      return getInitials(card.playerName);
    }
    return null;
  };

  const displayText = getDisplayText();
  const getLabel = () => {
    if (type === 'booster') {
      return 'Boost';
    } else if (type === 'bench') {
      return 'Bench';
    } else if (type === 'start') {
      return 'Starter';
    }
    return null;
  };

  return (
    <Grid w={fit === 'cover' ? '100%' : '60px'} gridTemplateColumns="1fr" gridTemplateRows="1fr 16px" gap="4px">
      <Box
        w="100%"
        aspectRatio="1/1"
        backgroundColor={displayContent.backgroundColor}
        borderRadius="26.5%"
        backgroundImage={displayContent.backgroundImage}
        backgroundSize={displayContent.backgroundSize}
        backgroundRepeat="no-repeat"
        backgroundPosition="center center"
        border={card || booster ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'}
        position="relative"
        {...props}
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0"
          backgroundColor="rgba(255, 255, 255, 0.5)"
          borderRadius="14px"
          animation={headshot && showAnimation ? `${onLoadAnimation} 0.3s ease-in-out forwards` : ''}
        />

        {displayContent.showIcon && displayContent.icon && (
          <Img
            src={displayContent.icon}
            pos="absolute"
            top="50%"
            left="50%"
            w="100%"
            h="100%"
            objectFit="contain"
            objectPosition="center center"
            transform="translate(-50%, -50%)"
          />
        )}

        {card?.playerInjuries && (
          <Box
            position="absolute"
            top="-7px"
            right="-5px"
            w="20px"
            h="20px"
            color="rgba(36, 36, 36, 1)"
            fontSize="8px"
            lineHeight="1"
            fontWeight="bold"
            fontFamily="JetBrains Mono"
            display="flex"
            textTransform="uppercase"
            alignItems="center"
            justifyContent="center"
            borderRadius="50%"
            backgroundColor="rgba(255, 165, 0, 1)"
          >
            gtd
          </Box>
        )}
      </Box>

      {!noLabel && (
        <Box w="100%" textAlign="center" overflow="hidden" maxW="100%" px="2px">
          <Text
            color="white"
            opacity="0.8"
            fontSize={{ base: "10px", md: "12px" }}
            fontWeight="500"
            lineHeight="1.2"
            textTransform="capitalize"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            maxW="100%"
          >
            {displayText ? displayText : getLabel()}
          </Text>
        </Box>)}
    </Grid>
  );
};

export default CardPlaceholder;
